import { useCallback, useMemo, useState } from 'react';

/**
 * Controlled multi-row selection state for tables. Tracks a Set of row ids and
 * exposes toggles plus a `toggleAll` over the currently-visible ids.
 */
export function useRowSelection() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((visibleIds: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return useMemo(
    () => ({ selectedIds, toggleRow, toggleAll, clear, count: selectedIds.size }),
    [selectedIds, toggleRow, toggleAll, clear],
  );
}
